export interface CommentThreadFields {
  id: number;
  parentCommentId?: number | null;
  createdAt: string;
}

export type ProfileCommentStatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

export interface ProfileCommentStatusFields {
  isApproved?: boolean | null;
  rating?: number | null;
  commentText?: string | null;
  ratingOnly?: boolean;
}

/** Matches backend ApplyCommentStatusFilter / ApplyLibraryBookCommentStatusFilter. */
export function matchesProfileCommentStatusFilter(
  comment: ProfileCommentStatusFields,
  filter: ProfileCommentStatusFilter
): boolean {
  if (filter === 'all') return true;
  const isRateOnly = !!(
    comment.ratingOnly ||
    (comment.rating != null && !comment.commentText?.trim())
  );
  if (filter === 'approved') {
    return comment.isApproved === true || (isRateOnly && comment.isApproved !== false);
  }
  if (filter === 'rejected') {
    return comment.isApproved === false;
  }
  if (filter === 'pending') {
    return (comment.isApproved === null || comment.isApproved === undefined) && !isRateOnly;
  }
  return true;
}

/** Profile list rows: roots always; own replies also appear in pending/approved/rejected tabs. */
export function isProfileCommentListRow(
  comment: { parentCommentId?: number | null },
  filter: ProfileCommentStatusFilter
): boolean {
  return true;
}

export interface CommentThreadNode<T extends CommentThreadFields> {
  comment: T;
  replies: CommentThreadNode<T>[];
}

const MAX_VISUAL_DEPTH = 3;
const INDENT_PX = 14;

/** Cap nested indent so replies stay readable inside a constrained modal width. */
export function commentThreadIndent(depth: number): number {
  return Math.min(Math.max(depth, 0), MAX_VISUAL_DEPTH) * INDENT_PX;
}

/** Per-level indent for nested DOM (does not accumulate past the visual cap). */
export function commentThreadIndentStep(depth: number): number {
  return depth > 0 && depth <= MAX_VISUAL_DEPTH ? INDENT_PX : 0;
}

/** Fill missing parentWriterName from loaded thread rows (profile list badges). */
export function enrichCommentsWithParentWriterNames<
  T extends { id: number; parentCommentId?: number | null; parentWriterName?: string; writerName?: string | null }
>(comments: T[]): T[] {
  const byId = new Map(comments.map((c) => [c.id, c]));
  return comments.map((comment) => {
    if (comment.parentCommentId == null || comment.parentWriterName?.trim()) {
      return comment;
    }
    const parent = byId.get(comment.parentCommentId);
    if (!parent?.writerName?.trim()) return comment;
    return { ...comment, parentWriterName: parent.writerName };
  });
}

export function readCommentListTotalCount(result: Record<string, unknown>): number | undefined {
  const raw = result.totalCount ?? result.TotalCount;
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, raw);
  if (typeof raw === 'string' && raw.trim()) {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return Math.max(0, parsed);
  }
  return undefined;
}

/** Normalize paginated comment API payloads (camelCase or PascalCase). */
export function normalizeCommentListPage<T extends { id?: number; isApproved?: boolean | null }>(
  result: Record<string, unknown> | null | undefined
): {
  comments: T[];
  totalCount: number | undefined;
  hasMore: boolean;
  pageNumber: number;
} {
  if (!result || typeof result !== 'object') {
    return { comments: [], totalCount: undefined, hasMore: false, pageNumber: 1 };
  }
  const rawComments = (result.comments ?? result.Comments ?? []) as Array<Record<string, unknown>>;
  const comments = rawComments.map((row) => {
    const isApprovedRaw = row.isApproved ?? row.IsApproved;
    const idRaw = row.id ?? row.Id;
    const parentRaw = row.parentCommentId ?? row.ParentCommentId;
    return {
      ...row,
      id: typeof idRaw === 'number' ? idRaw : Number(idRaw),
      parentCommentId:
        parentRaw == null || parentRaw === ''
          ? null
          : typeof parentRaw === 'number'
            ? parentRaw
            : Number(parentRaw),
      isApproved:
        isApprovedRaw === true || isApprovedRaw === 'true'
          ? true
          : isApprovedRaw === false || isApprovedRaw === 'false'
            ? false
            : isApprovedRaw == null
              ? null
              : Boolean(isApprovedRaw)
    } as T;
  });
  const rawPage = result.pageNumber ?? result.PageNumber;
  const pageNumber = typeof rawPage === 'number' && Number.isFinite(rawPage) ? rawPage : 1;
  return {
    comments,
    totalCount: readCommentListTotalCount(result),
    hasMore: result.hasMore === true || result.HasMore === true,
    pageNumber
  };
}

/** Unique approved rows (roots and replies); excludes pending/rejected. */
export function countApprovedComments<T extends { id: number; isApproved?: boolean | null }>(
  comments: Iterable<T>
): number {
  const seen = new Set<number>();
  let count = 0;
  for (const comment of comments) {
    if (comment.isApproved !== true || seen.has(comment.id)) continue;
    seen.add(comment.id);
    count += 1;
  }
  return count;
}

/** Direct + nested reply count keyed by each comment id (for profile cards and thread badges). */
export function countCommentDescendantsById<T extends { id: number; parentCommentId?: number | null }>(
  all: T[]
): Map<number, number> {
  const childrenByParent = new Map<number, number[]>();
  for (const c of all) {
    if (c.parentCommentId != null) {
      const arr = childrenByParent.get(c.parentCommentId) ?? [];
      arr.push(c.id);
      childrenByParent.set(c.parentCommentId, arr);
    }
  }

  const memo = new Map<number, number>();
  const dfs = (commentId: number): number => {
    if (memo.has(commentId)) return memo.get(commentId)!;
    const children = childrenByParent.get(commentId) ?? [];
    let sum = children.length;
    for (const childId of children) sum += dfs(childId);
    memo.set(commentId, sum);
    return sum;
  };

  const result = new Map<number, number>();
  for (const c of all) {
    result.set(c.id, dfs(c.id));
  }
  return result;
}

export function buildCommentTree<T extends CommentThreadFields>(comments: T[]): CommentThreadNode<T>[] {
  const byId = new Map<number, CommentThreadNode<T>>();
  // Precompute timestamps once to avoid allocating `Date` objects inside sort comparators.
  const createdAtMsById = new Map<number, number>();
  for (const comment of comments) {
    const ms = new Date(comment.createdAt).getTime();
    createdAtMsById.set(comment.id, Number.isFinite(ms) ? ms : 0);
    byId.set(comment.id, { comment, replies: [] });
  }

  const roots: CommentThreadNode<T>[] = [];
  for (const node of byId.values()) {
    const parentId = node.comment.parentCommentId ?? null;
    if (parentId != null && byId.has(parentId)) {
      byId.get(parentId)!.replies.push(node);
    } else {
      roots.push(node);
    }
  }

  const byNewest = (a: CommentThreadNode<T>, b: CommentThreadNode<T>) =>
    (createdAtMsById.get(b.comment.id)! - createdAtMsById.get(a.comment.id)!);
  const byOldest = (a: CommentThreadNode<T>, b: CommentThreadNode<T>) =>
    (createdAtMsById.get(a.comment.id)! - createdAtMsById.get(b.comment.id)!);

  const sortNodes = (nodes: CommentThreadNode<T>[], isRoot: boolean) => {
    nodes.sort(isRoot ? byNewest : byOldest);
    for (const node of nodes) {
      sortNodes(node.replies, false);
    }
  };

  sortNodes(roots, true);
  return roots;
}
